import { ArrowLeft } from '@tailgrids/icons';
import type React from 'react';
import { Link } from 'react-router-dom';
import { buttonStyles } from '../tailgrids/core/button.js';
import { Card } from '../tailgrids/core/card.js';
import { ErrorFaceIcon } from './status-icons.js';

interface StatusPageAction {
  label: string;
  /** In-app path: renders a link. */
  to?: string;
  /** Click handler: renders a button (reload, retry). Give one of `to` or `onPress`. */
  onPress?: () => void;
  /** The template's left arrow ("Back to home"). Turn off for an action that does not go back (Reload). */
  leadingArrow?: boolean;
}

interface StatusPageProps {
  title: string;
  /** One or two short lines under the title. */
  description: React.ReactNode;
  /** The picture above the title; defaults to the template's error face. Pass a `SentIcon` for a confirmation. */
  illustration?: React.ReactNode;
  /** The single primary action. */
  action: StatusPageAction;
}

/**
 * A page that says one thing and offers one next step: not found, the error boundary, a confirmation, maintenance.
 * Template example: NextAdmin `(pages)/error-page/page.tsx` and `(pages)/mail-success/page.tsx` (demo `/error-page`,
 * `/mail-success`, `/coming-soon`, `/maintenance`): one centred card, illustration, `text-4xl` title, grey text, one
 * primary action with a leading arrow.
 */
export function StatusPage({ title, description, illustration, action }: StatusPageProps) {
  const actionClass = buttonStyles({ size: 'lg', className: 'mt-6 gap-1.5 px-3.5 text-sm' });
  const content = (
    <>
      {action.leadingArrow !== false && <ArrowLeft className="size-5" />}
      <span className="px-0.5 text-sm leading-5 font-medium tracking-[-0.15px] whitespace-nowrap">{action.label}</span>
    </>
  );

  return (
    <Card className="relative flex flex-col items-center justify-center overflow-clip rounded-xl border-[0.5px] border-border-secondary bg-card-background p-5">
      <div className="relative flex w-full flex-col items-center justify-center pt-10 pb-20">
        {/* Illustration */}
        <div className="relative flex h-auto w-full max-w-125 flex-col items-center justify-center overflow-clip rounded-[746px] border border-border-secondary/20">
          {illustration ?? <ErrorFaceIcon className="h-auto w-full" />}
        </div>

        {/* Text */}
        <div className="mt-20 flex flex-col items-center gap-3 text-center">
          <h2 className="text-4xl leading-10 font-semibold text-text-primary">{title}</h2>
          <div className="text-sm leading-5 font-normal tracking-[-0.15px] text-text-tertiary">{description}</div>
        </div>

        {/* Action */}
        {action.to !== undefined ? (
          <Link to={action.to} className={actionClass}>
            {content}
          </Link>
        ) : (
          <button type="button" onClick={action.onPress} className={actionClass}>
            {content}
          </button>
        )}
      </div>
    </Card>
  );
}
